import { useEffect, useState } from 'react';
import { Drawer, Form, Input, InputNumber, Switch, Select, Button, Space,
         Divider, message, Tabs } from 'antd';
import { api } from '../api';
import PropertyEditor from './PropertyEditor';
import MediaEditor from './MediaEditor';
import TranslationsEditor from './TranslationsEditor';

const TAX_RATES = [
  { label: '7 % MwSt', value: 7 },
  { label: '19 % MwSt', value: 19 },
];

function calcNet(gross, taxRate) {
  if (gross == null || !taxRate) return null;
  return Math.round((gross / (1 + taxRate / 100)) * 100) / 100;
}

export default function ProductForm({ product, storeId, meta, onSave, onClose }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [properties, setProperties] = useState([]);
  const [categoryIds, setCategoryIds] = useState([]);
  const [componentIds, setComponentIds] = useState([]);
  const [addonIds, setAddonIds] = useState([]);
  const [translations, setTranslations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [grossPrice, setGrossPrice] = useState(null);
  const [vatRate, setVatRate] = useState(7);

  const selectedTypeId = Form.useWatch('fk_product_type', form);
  const currentName = Form.useWatch('name', form);
  const selectedType = meta.productTypes.find(pt => pt.id === selectedTypeId);
  const typeName = selectedType?.name || '';

  const isNew = !product?.id;

  useEffect(() => {
    api.categories.list(storeId).then(setCategories).catch(() => {});
    api.products.list(storeId).then(setAllProducts).catch(() => {});
  }, [storeId]);

  useEffect(() => {
    if (product) {
      form.setFieldsValue({
        name:            product.name,
        fk_product_type: product.fk_product_type,
        net_price:       product.net_price,
        stock:           product.stock,
        is_purchasable:  product.is_purchasable ?? true,
        fk_base_product: product.fk_base_product,
      });
      setProperties(product.properties || []);
      setCategoryIds((product.categories || []).map(c => c.id));
      setComponentIds((product.components || []).filter(c => !c.inherited).map(c => c.id));
      setAddonIds((product.addons || []).filter(a => !a.inherited).map(a => a.id));
      setTranslations(product.translations || []);
      setGrossPrice(null);
    } else {
      form.resetFields();
      setProperties([]);
      setCategoryIds([]);
      setGrossPrice(null);
      setComponentIds([]);
      setAddonIds([]);
      setTranslations([]);
    }
  }, [product, form]);

  async function handleSave() {
    try {
      const values = await form.validateFields();
      setSaving(true);

      // variants keep fk_base_product; components/addons are linked via junction tables
      if (typeName !== 'variant') values.fk_base_product = null;

      const payload = {
        ...values,
        fk_store:      storeId,
        properties:    properties.filter(p => !p.inherited).map(p => ({
          fk_property: p.fk_property,
          value:       p.value,
        })),
        category_ids:  categoryIds,
        component_ids: typeName === 'variant' || typeName === 'component' || typeName === 'addon'
          ? undefined   // sub-products don't own components/addons themselves
          : componentIds,
        addon_ids:     typeName === 'variant' || typeName === 'component' || typeName === 'addon'
          ? undefined
          : addonIds,
        translations,
      };

      const saved = isNew
        ? await api.products.create(payload)
        : await api.products.update(product.id, payload);

      message.success('Gespeichert');
      onSave(saved);
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err.message || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  }

  const availableComponents = allProducts.filter(
    p => p.product_type_name === 'component' && p.id !== product?.id
  );
  const availableAddons = allProducts.filter(
    p => p.product_type_name === 'addon' && p.id !== product?.id
  );
  const availableBaseProducts = allProducts.filter(p => p.id !== product?.id);

  const isSubProduct = typeName === 'variant' || typeName === 'component' || typeName === 'addon';
  const showCategories = !isSubProduct || typeName === 'addon' || typeName === 'variant';

  const tabs = [
    {
      key: 'main',
      label: 'Stammdaten',
      children: (
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Name">
            <Input />
          </Form.Item>
          <Form.Item name="fk_product_type" label="Produkttyp"
            rules={[{ required: true, message: 'Pflichtfeld' }]}>
            <Select options={meta.productTypes.map(pt => ({ value: pt.id, label: pt.name }))} />
          </Form.Item>

          {typeName === 'variant' && (
            <Form.Item name="fk_base_product" label="Basisprodukt">
              <Select
                showSearch allowClear
                placeholder="Basisprodukt wählen…"
                options={availableBaseProducts.map(p => ({ value: p.id, label: p.name || p.id }))}
                filterOption={(input, opt) =>
                  (opt?.label || '').toLowerCase().includes(input.toLowerCase())}
              />
            </Form.Item>
          )}

          <Form.Item name="net_price" label="Nettopreis (€)">
            <InputNumber min={0} precision={2} step={0.01} style={{ width: '100%' }}
              decimalSeparator="," />
          </Form.Item>
          <Form.Item label="Bruttopreis (€) → Nettopreis berechnen">
            <Space.Compact style={{ width: '100%' }}>
              <InputNumber
                min={0} precision={2} step={0.01} decimalSeparator=","
                placeholder="Bruttopreis (€)" value={grossPrice}
                style={{ width: '60%' }}
                onChange={v => {
                  setGrossPrice(v);
                  const net = calcNet(v, vatRate);
                  if (net != null) form.setFieldsValue({ net_price: net });
                }}
              />
              <Select
                value={vatRate} options={TAX_RATES}
                style={{ width: '40%' }}
                onChange={r => {
                  setVatRate(r);
                  const net = calcNet(grossPrice, r);
                  if (net != null) form.setFieldsValue({ net_price: net });
                }}
              />
            </Space.Compact>
          </Form.Item>
          <Form.Item name="stock" label="Lagerbestand">
            <InputNumber min={0} precision={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="is_purchasable" label="Separat erhältlich" valuePropName="checked">
            <Switch />
          </Form.Item>

          {showCategories && (
            <>
              <Divider />
              <Form.Item label="Kategorien">
                <Select
                  mode="multiple" value={categoryIds} onChange={setCategoryIds}
                  options={categories.map(c => ({ value: c.id, label: c.name }))}
                  placeholder="Kategorien zuordnen…" showSearch
                  filterOption={(input, opt) =>
                    opt.label.toLowerCase().includes(input.toLowerCase())}
                />
              </Form.Item>
            </>
          )}
          {!isSubProduct && (
            <>
              <Form.Item label="Komponenten">
                <Select
                  mode="multiple" value={componentIds} onChange={setComponentIds}
                  options={availableComponents.map(p => ({
                    value: p.id, label: p.name || p.id,
                  }))}
                  placeholder="Komponenten zuordnen…" showSearch
                  filterOption={(input, opt) =>
                    (opt?.label || '').toLowerCase().includes(input.toLowerCase())}
                />
              </Form.Item>
              <Form.Item label="Addons">
                <Select
                  mode="multiple" value={addonIds} onChange={setAddonIds}
                  options={availableAddons.map(p => ({
                    value: p.id, label: p.name || p.id,
                  }))}
                  placeholder="Addons zuordnen…" showSearch
                  filterOption={(input, opt) =>
                    (opt?.label || '').toLowerCase().includes(input.toLowerCase())}
                />
              </Form.Item>
            </>
          )}
        </Form>
      ),
    },
    {
      key: 'properties',
      label: 'Eigenschaften',
      children: (
        <PropertyEditor
          properties={properties}
          allProperties={meta.properties}
          onChange={setProperties}
        />
      ),
    },
    {
      key: 'translations',
      label: 'Texte',
      children: (
        <TranslationsEditor translations={translations} onChange={setTranslations} defaultName={currentName || ''} />
      ),
    },
    {
      key: 'media',
      label: 'Medien',
      children: <MediaEditor productId={product?.id} />,
    },
  ];

  return (
    <Drawer
      title={isNew ? 'Neues Produkt' : (product?.name || 'Produkt bearbeiten')}
      open onClose={onClose} width={600}
      footer={
        <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
          <Button onClick={onClose}>Abbrechen</Button>
          <Button type="primary" onClick={handleSave} loading={saving}>Speichern</Button>
        </Space>
      }
    >
      <Tabs items={tabs} />
    </Drawer>
  );
}
